import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, MailCheck } from "lucide-react";
import Seo from "../components/Seo";
import AuthShell from "../components/AuthShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Forgot password" description="Reset your StUpEco Hub password." path="/forgot-password" />
      <AuthShell
        title={sent ? "Check your email" : "Reset your password"}
        subtitle={sent ? "If an account exists for that email, we've sent a reset link. It expires in 1 hour." : "Enter your email and we'll send you a reset link."}
        footer={<>Remembered it? <Link to="/login" className="font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline">Back to login</Link></>}
      >
        {sent ? (
          <div className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5" data-testid="forgot-sent">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-green)]/12 text-[color:var(--brand-green)]">
              <MailCheck className="h-5 w-5" />
            </span>
            <p className="text-sm text-[color:var(--ink-soft)]">Reset link sent to <span className="font-medium text-[color:var(--ink)]">{email}</span>.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11 rounded-xl" />
            </div>
            <button type="submit" disabled={submitting} data-testid="forgot-submit" className="group inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send reset link <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
            </button>
          </form>
        )}
      </AuthShell>
    </>
  );
}
